import React, { useState } from 'react';
import Swal from '../librarianSwal';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';

import {
  addStudent,
  getErrorMessage
} from '../../api/api';
import '../lib-page-header.css';

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim());

  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim());
    const row = {};

    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    return row;
  });
};

const StudentImport = () => {
  const libraryId = useActiveLibraryId();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;
      const parsedRows = parseCSV(text);
      setRows(parsedRows);
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!libraryId) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return;
    }

    if (rows.length === 0) {
      Swal.fire('Error', 'Please select CSV file first', 'error');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Import Students?',
      text: `${rows.length} students will be imported.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Import'
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);

      for (const row of rows) {
        const payload = {
          library_id: libraryId,
          admission_no: row.admission_no,
          gr_number: row.gr_number || row.gr_no || null,
          student_name: row.student_name,
          class_name: row.class_name,
          section: row.section,
          guardian_name: row.guardian_name,
          guardian_mobile: row.guardian_mobile,
          guardian_email: row.guardian_email || null,
          status: row.status || 'active'
        };

        await addStudent(payload);
      }

      Swal.fire('Success', 'Students imported successfully', 'success');
      setRows([]);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = () => {
    const csv = [
      'admission_no,gr_number,student_name,class_name,section,guardian_name,guardian_mobile,guardian_email,status',
      'ADM001,GR-1001,Rahul Sharma,5,A,Ramesh Sharma,9876543210,parent@example.com,active'
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_sample.csv';
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-file-import" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Student Import</h3>
            <div className="lib-page-header__subtitle">Import ERP/student data using CSV</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button className="btn btn-outline-primary" onClick={downloadSample}>
            Download Sample CSV
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3 table-full-card">
        <div className="card-body">
          <label className="form-label">Upload CSV File</label>
          <div className="input-group">
            <input
              type="file"
              accept=".csv"
              className="form-control"
              onChange={handleFileChange}
            />
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={loading || rows.length === 0}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-1" />Importing...</>
              ) : (
                <><i className="fas fa-file-import me-1" />Import Students</>
              )}
            </button>
          </div>

          <small className="text-muted">
            Required columns: admission_no, student_name, class_name, section,
            guardian_name, guardian_mobile. Optional: gr_number
          </small>
        </div>
      </div>

      <div className="card shadow-sm table-full-card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-bold"><i className="fas fa-table me-2" />Preview</span>

          <button
            className="btn btn-primary btn-sm"
            onClick={handleImport}
            disabled={loading || rows.length === 0}
          >
            {loading ? 'Importing...' : 'Import Students'}
          </button>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Admission No</th>
                  <th>GR Number</th>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Section</th>
                  <th>Guardian</th>
                  <th>Mobile</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{row.admission_no}</td>
                    <td>{row.gr_number || row.gr_no || '-'}</td>
                    <td>{row.student_name}</td>
                    <td>{row.class_name}</td>
                    <td>{row.section}</td>
                    <td>{row.guardian_name}</td>
                    <td>{row.guardian_mobile}</td>
                  </tr>
                ))}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No CSV data selected
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentImport;